/**
 * Surface-agnostic thread state: one root plus its replies, paged older on
 * demand. Space channels and conversations wrap this with their own fetcher
 * and socket event wiring.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PublicMessageThread } from '@adieuu/shared';
import { applyThreadSummary } from '../../services/threadSummary';

export const THREAD_PAGE_SIZE = 50;

const OBJECT_ID_HEX = /^[0-9a-f]{24}$/i;

/** `?thread=` must be a 24-char hex id; anything else is ignored. */
export function parseThreadParam(raw: string | null | undefined): string | null {
  return raw && OBJECT_ID_HEX.test(raw) ? raw.toLowerCase() : null;
}

export type ThreadStatus = 'idle' | 'loading' | 'ready' | 'error' | 'not_found';

export interface ThreadMessageLike {
  id: string;
  threadRootId?: string;
  thread?: PublicMessageThread;
}

export interface ThreadPage<M> {
  root: M;
  /** Oldest-first. */
  replies: M[];
  cursor: string | null;
}

export type ThreadFetchResult<P> = { ok: true; page: P } | { ok: false; notFound: boolean };

export interface MessageThreadState<M> {
  status: ThreadStatus;
  root: M | null;
  /** Oldest-first. */
  replies: M[];
  cursor: string | null;
  loadingOlder: boolean;
}

function emptyState<M>(): MessageThreadState<M> {
  return { status: 'idle', root: null, replies: [], cursor: null, loadingOlder: false };
}

export function useMessageThread<M extends ThreadMessageLike, P extends ThreadPage<M>>(params: {
  /** Channel or conversation id; every fetch is scoped to it. */
  scopeId: string | undefined;
  /** Any message in the thread; the server resolves the root. */
  messageId: string | null;
  fetchPage: (messageId: string, options: { before?: string; limit: number }) => Promise<ThreadFetchResult<P>>;
  /** Called with every page that lands (author resolution, member upserts). */
  onPage?: (page: P) => void;
  /** Whether a message (with a matching `threadRootId`) belongs to the loaded root's scope. */
  sameScope?: (message: M, root: M) => boolean;
  markDeleted: (message: M) => M;
  pageSize?: number;
}) {
  const { scopeId, messageId } = params;
  const pageSize = params.pageSize ?? THREAD_PAGE_SIZE;
  const [state, setState] = useState<MessageThreadState<M>>(emptyState);
  const requestRef = useRef(0);
  const fetchRef = useRef(params.fetchPage);
  fetchRef.current = params.fetchPage;
  const onPageRef = useRef(params.onPage);
  onPageRef.current = params.onPage;
  const sameScopeRef = useRef(params.sameScope);
  sameScopeRef.current = params.sameScope;
  const markDeletedRef = useRef(params.markDeleted);
  markDeletedRef.current = params.markDeleted;
  const stateRef = useRef(state);
  stateRef.current = state;
  const loadedScopeRef = useRef<string | undefined>(undefined);

  const load = useCallback(
    async (force = false) => {
      const loadedRoot = stateRef.current.root;
      // ?thread= is rewritten to the resolved root after a load; that is not a new thread.
      if (!force && loadedRoot && loadedRoot.id === messageId && loadedScopeRef.current === scopeId) return;
      const token = ++requestRef.current;
      if (!scopeId || !messageId) {
        loadedScopeRef.current = undefined;
        setState(emptyState());
        return;
      }
      setState({ ...emptyState<M>(), status: 'loading' });
      try {
        const res = await fetchRef.current(messageId, { limit: pageSize });
        if (token !== requestRef.current) return;
        if (!res.ok) {
          setState({ ...emptyState<M>(), status: res.notFound ? 'not_found' : 'error' });
          return;
        }
        onPageRef.current?.(res.page);
        loadedScopeRef.current = scopeId;
        setState({
          status: 'ready',
          root: res.page.root,
          replies: res.page.replies,
          cursor: res.page.cursor,
          loadingOlder: false,
        });
      } catch {
        if (token === requestRef.current) setState({ ...emptyState<M>(), status: 'error' });
      }
    },
    [scopeId, messageId, pageSize],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const loadOlder = useCallback(async () => {
    const current = stateRef.current;
    if (!current.root || !current.cursor || current.loadingOlder || !scopeId) return;
    const token = requestRef.current;
    setState((s) => ({ ...s, loadingOlder: true }));
    try {
      const res = await fetchRef.current(current.root.id, { before: current.cursor, limit: pageSize });
      if (token !== requestRef.current) return;
      if (!res.ok) {
        setState((s) => ({ ...s, loadingOlder: false }));
        return;
      }
      onPageRef.current?.(res.page);
      const older = res.page.replies;
      setState((s) => {
        const known = new Set(s.replies.map((m) => m.id));
        return {
          ...s,
          replies: [...older.filter((m) => !known.has(m.id)), ...s.replies],
          cursor: res.page.cursor,
          loadingOlder: false,
        };
      });
    } catch {
      if (token === requestRef.current) setState((s) => ({ ...s, loadingOlder: false }));
    }
  }, [scopeId, pageSize]);

  /**
   * Add or update a reply (own send or socket). The root summary is left alone:
   * it is replaced from the server's copy carried on socket events.
   */
  const ingestReply = useCallback((msg: M) => {
    setState((s) => {
      const root = s.root;
      if (!root || msg.threadRootId !== root.id) return s;
      if (sameScopeRef.current && !sameScopeRef.current(msg, root)) return s;
      const idx = s.replies.findIndex((m) => m.id === msg.id);
      if (idx !== -1) {
        const replies = [...s.replies];
        replies[idx] = { ...replies[idx]!, ...msg };
        return { ...s, replies };
      }
      return { ...s, replies: [...s.replies, msg] };
    });
  }, []);

  /** Replace the root's summary when `rootId` is the loaded root. */
  const applyRootSummary = useCallback((rootId: string, summary: PublicMessageThread | null) => {
    setState((s) => (s.root && s.root.id === rootId ? { ...s, root: applyThreadSummary(s.root, summary) } : s));
  }, []);

  const patchMessage = useCallback((messageId: string, patch: Partial<M>) => {
    setState((s) => {
      if (!s.root) return s;
      if (s.root.id === messageId) return { ...s, root: { ...s.root, ...patch } };
      const idx = s.replies.findIndex((m) => m.id === messageId);
      if (idx === -1) return s;
      const replies = [...s.replies];
      replies[idx] = { ...replies[idx]!, ...patch };
      return { ...s, replies };
    });
  }, []);

  const markMessageDeleted = useCallback((messageId: string) => {
    setState((s) => {
      if (!s.root) return s;
      const mark = markDeletedRef.current;
      const root = s.root.id === messageId ? mark(s.root) : s.root;
      const hit = s.replies.some((m) => m.id === messageId);
      if (root === s.root && !hit) return s;
      const replies = hit ? s.replies.map((m) => (m.id === messageId ? mark(m) : m)) : s.replies;
      return { ...s, root, replies };
    });
  }, []);

  const reload = useCallback(() => load(true), [load]);

  return {
    ...state,
    stateRef,
    reload,
    loadOlder,
    ingestReply,
    applyRootSummary,
    patchMessage,
    markMessageDeleted,
  };
}
